/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

import {
  AfterViewInit,
  Attribute, Directive, ElementRef, Input, Renderer2, OnDestroy,
} from '@angular/core';
import { RecordEventService } from './record-event.service';
import { TelemetryEvent } from './telemetry.d';

/**
 * A directive to record a telemetry event from user interaction.
 *
 * The directive accepts a TelemetryEvent object,
 * or will read the element's data attributes for event data.
 */
@Directive({
  selector: 'bulbRecordEvent, [bulbRecordEvent]',
  standalone: false,
})
export class RecordEventDirective implements AfterViewInit, OnDestroy {
  @Input('bulbRecordEvent') event: TelemetryEvent | string = 'click';

  /**
   * Reference to event listened for removal.
   */
  private eventRef: () => void;

  constructor(
    private elRef: ElementRef,
    private renderer: Renderer2,
    private eventSvc: RecordEventService,
    @Attribute('data-event-action') public action: string,
    @Attribute('data-event-category') public category: string = null,
    @Attribute('data-event-label') public label: string = null,
    @Attribute('data-event-value') public value: Number = null,
  ) { }

  ngAfterViewInit(): void {
    // If the event is a TelemetryEvent, we use those properties values isntead of data attributes.
    if (typeof this.event !== 'string') {
      this.value = this.event.value;
      this.label = this.event.label;
      this.category = this.event.category;
      this.action = this.event.action;
      this.event = this.event.name;
    }
    // Default event name is 'click'.
    this.event = this.event || 'click';
    // Make sure this value is a Number, not string.
    this.value = Number(this.value);

    this.eventRef = this.renderer.listen(this.elRef.nativeElement, this.event, () => {
      this.eventSvc.recordEvent(this.action, this.category, this.label, this.value);
    });
  }

  ngOnDestroy(): void {
    this.eventRef();
  }
}
